import { EventEmitter } from 'events';
import RealtimeError from 'app/services/rtc/RealtimeError';
import RTCRoom from 'app/services/rtc/Room';
import { Stream, IEncodedStreamActionTypeMap } from '@engoo/realtime-protocol/stream';
import { IEncodedPersistedAction, StreamPosition, StreamPositionCodec } from '@engoo/realtime-protocol/persisted';
import * as iots from 'io-ts';
import { decodeOrThrow } from 'app/utils/ioTs';

export enum ActionStreamEvents {
  ACTION           = 'action',
  LOCK             = 'lock',
  METADATA_CHANGED = 'metadata_changed',
}

export enum BlockReason {
  QuotaExceeded = 'quotaExceeded',

  // Sent by server for lesson teachers after lesson end.
  // This is a temporary back-compat workaround until frontend fully understands authorize vs. join.
  // Once it does, being authorized, but not joined will imply this
  TransitionalLessonEnd = 'transitionalLessonEnd',
}

export enum StreamActions {
  LOCK      = 'lockStream',
  INSERT    = 'insertAction',
  APPEND    = 'appendAction',
  SUBSCRIBE = 'subscribe',
}

export enum StreamEvents {
  ACTION           = 'action',
  METADATA_CHANGED = 'streamMetadataChanged',
}

export interface IMetadata {
  blockedReasons: BlockReason[];
}

export interface IActionStreamEventTypeTable<ActionT> {
  [ActionStreamEvents.ACTION]: ActionT & IEncodedPersistedAction;
  [ActionStreamEvents.LOCK]: StreamPosition;
  [ActionStreamEvents.METADATA_CHANGED]: IMetadata;
}

const LockResponseCodec = iots.interface({
  position: StreamPositionCodec,
});

export interface IActionStream<ActionT> {
  metadata: IMetadata | undefined;

  // Acquire a lock to append to a stream in order
  lock(): Promise<{ position: StreamPosition }>;

  // Submit an event at an exact timeslot.
  // Returns the persisted event, including the additional fields
  insert<A extends ActionT>(
    action: A,
    position: StreamPosition,
  ): Promise<A & IEncodedPersistedAction>;

  // Submit an event at the current place in the channel. Always accepted.
  // Returns the persisted event, including the additional fields
  append<A extends ActionT>(msg: A): Promise<A & IEncodedPersistedAction>;

  // Subscribe to action and metadata updates for the stream.
  // Returns stream metadata and actions that make up current stream state as part of the result.
  // If lastPosition is provided, the returned actions will correspond to positions later than lastPosition.
  subscribe(lastPosition?: StreamPosition): Promise<{
    deltaActions: Array<ActionT & IEncodedPersistedAction>;
    metadata: IMetadata;
  }>;

  on<A extends keyof IActionStreamEventTypeTable<ActionT>>(
    action: A,
    handler: (value: IActionStreamEventTypeTable<ActionT>[A]) => void,
  ): void;

  removeListener<A extends keyof IActionStreamEventTypeTable<ActionT>>(
    action: A,
    handler: (value: IActionStreamEventTypeTable<ActionT>[A]) => void,
  ): void;
}

export type EncodedAction<StreamName extends Stream> = IEncodedStreamActionTypeMap[StreamName];

// Used by Room to coordinate actions multiplexed over
// rtc signalling channel.
export class ActionStream<StreamName extends Stream>
  extends EventEmitter
  implements IActionStream<EncodedAction<StreamName>> {
  private room: RTCRoom;
  private name: StreamName;
  public metadata: IMetadata | undefined;

  public constructor(room: RTCRoom, name: StreamName, metadata?: IMetadata) {
    super();

    this.room = room;
    this.name = name;

    this.metadata = metadata;
    this.addListener(ActionStreamEvents.METADATA_CHANGED, newMetadata => {
      this.metadata = newMetadata;
    });
  }

  public lock() {
    return this.wrapRealtimeErrors(async () => {
      return decodeOrThrow(
        LockResponseCodec,
        await this.room.send(StreamActions.LOCK, { stream: this.name }),
      );
    });
  }

  public insert<A extends EncodedAction<StreamName>>(action: A, position: StreamPosition) {
    return this.wrapRealtimeErrors(async () =>
      this.room.send(
        StreamActions.INSERT,
        { stream: this.name, action, position },
      ),
    );
  }

  public append<A extends EncodedAction<StreamName>>(action: A) {
    return this.wrapRealtimeErrors(async () => {
      return this.room.send(StreamActions.APPEND, { stream: this.name, action });
    });
  }

  public subscribe(lastPosition: StreamPosition) {
    return this.wrapRealtimeErrors(async () => {
      return this.room.subscribeToStream(this.name, lastPosition);
    });
  }

  private async wrapRealtimeErrors<R>(f: () => Promise<R>) {
    try {
      return await f();
    } catch (e) {
      throw RealtimeError.tryParse(e) || e;
    }
  }
}
